// @avantia common-code
import { ensureDefined, SMap } from '@avantia/client-and-server-utilities';
import { isDefined } from '@avantia/form-validation';
import { lodash } from '@avantia/lodash';
import { hasOwnProperty, HpErrorOr } from '@avantia/logging-and-errors';
import {
  getAnswersFromAnswerMap as getAnswersFromAnswerMapGeneric,
  HpAnswerMap,
  HpAnswerValueTypes,
  HpGetAnswersFromAnswerMapOptions,
  setCustomerAnswer
} from '@avantia/questionset-model';
import { metadataId } from '../types';
import { getQuestionIdFor } from './questionIds';

export interface HpGetAnswersFromAnswerMapProps<QuestionIdT> {
  answers: HpAnswerMap;
  keys: (keyof QuestionIdT)[];
  options?: HpGetAnswersFromAnswerMapOptions;
  productId: string;
}

export function getAnswersFromAnswerMap<QuestionIdT>({
  answers,
  keys: keyNames,
  options,
  productId
}: HpGetAnswersFromAnswerMapProps<QuestionIdT>): Partial<QuestionIdT> {
  const qids: string[] = [];
  const qidToNameMap: SMap<string> = {};
  const mappedKeys = keyNames.map((nameProp) => {
    const name = nameProp as string;
    const questionId = getQuestionIdFor(productId, name);
    qids.push(questionId);
    qidToNameMap[questionId] = name;
    return questionId;
  }) as (keyof QuestionIdT)[];
  const result = getAnswersFromAnswerMapGeneric<QuestionIdT>(answers, mappedKeys, options);
  const resp: Partial<QuestionIdT> = {};
  qids.map((qid) => {
    const name = qidToNameMap[qid];
    resp[name] = result[qid];
  });

  return resp;
}

export interface HpGetAnswersFromAnswerMapUsingIdsProps {
  answers: HpAnswerMap;
  options?: HpGetAnswersFromAnswerMapOptions;
  questionIds: string[];
}

export function getAnswersFromAnswerMapUsingIds({
  answers,
  questionIds,
  options
}: HpGetAnswersFromAnswerMapUsingIdsProps): SMap<HpAnswerValueTypes> {
  const result = getAnswersFromAnswerMapGeneric<any>(answers, questionIds, options);
  return result;
}

export function toLowerCase<TValue extends string | null | undefined>(value: TValue): TValue {
  return (value ? value.toLowerCase() : value) as any;
}

export function areSame(a: string | undefined, b: string | undefined): boolean {
  if ((a === undefined || a === null) && (b === undefined || b === null)) {
    return true;
  }

  return `${a}`.toLowerCase() === `${b}`.toLowerCase();
}

export function ensureUnique<ElementT, KeyT>(list: ElementT[], getKey: (item: ElementT) => KeyT): HpErrorOr<KeyT[]> {
  const unique = new Map<KeyT, true>();
  list.forEach((item) => {
    const key = getKey(item);
    if (!unique.has(key)) {
      unique.set(key, true);
    } else {
      return { error: `Duplicate key found: '${key}'.`, result: null };
    }
  });

  return { result: [...unique.keys()] };
}

export function convertLogDataToJsonString(msg: unknown): string {
  let result: string | undefined;
  if (msg) {
    const msgType = typeof msg;
    if (msgType === 'string') {
      let message = (msg as string).trim();

      if (message.startsWith('{"message":"{"')) {
        message = JSON.parse(message).message;
      }

      if (message.startsWith('\x1B')) {
        // it contains console color commands.
        if (message.slice(5, 6) === '{') {
          // it is a colour-coded JSON message.
          return message;
        } else {
          // turn it into a color coded JSON message.
          const colorCode = message.slice(0, 5);
          return `${colorCode}${JSON.stringify({ message: message.slice(5) }, null, 2)}`;
        }
      } else if (message.startsWith('{') && message.endsWith('}')) {
        if (message.slice(1, 2) === '\n') {
          try {
            return JSON.stringify(JSON.parse(message)); // remove white space
          } catch (ex) {
            // handled further down
          }
        }
      }

      if (message.startsWith('[@')) {
        const ix = message.indexOf(']');
        if (ix > 0) {
          const rest = message.substr(ix + 1).trim();
          try {
            result = JSON.stringify({
              message: JSON.parse(rest)
            });
          } catch (ex) {
            result = JSON.stringify({
              message: rest
            });
          }
        }
      }
    } else if (msgType === 'object') {
      result = msg as any;
    }
  }

  return typeof result === 'string' ? result : JSON.stringify(result || { message: msg });
}

export function exceptionToString(ex: unknown): string {
  const { name, message } = ex as Error;
  if (message) {
    return name ? `${name}: ${message}` : message;
  }

  if (typeof ex === 'string') {
    return ex;
  }

  try {
    return JSON.stringify(ex);
  } catch (x) {
    const { message } = x as Error;
    return `An error with keys (${lodash.keys(ex)}) was thrown and could not be serialized: ${message}`;
  }
}

type HpVersionHistoryMetaData = Record<string, string>;

export interface HpMetaData {
  agentId?: string | undefined;
  begin?: string;
  callMediaUser?: string;
  isJourneyComplete?: true | undefined;
  productId: string;
  questionSetId: string;
  source?: string | null;
  version: string;
  versionHistory?: HpVersionHistoryMetaData;
}

export function getMetaDataOrUndefinedFromAnswers<MetaDataT extends HpMetaData>(
  productId: string,
  answers: HpAnswerMap
): MetaDataT | undefined {
  const { metadata } = getAnswersFromAnswerMap({ productId, answers, keys: [metadataId] });
  if (metadata) {
    return parseMetaData<MetaDataT>(metadata, false);
  }
}

export function getMetaDataFromAnswers<MetaDataT extends HpMetaData>(
  productId: string,
  answers: HpAnswerMap,
  validate?: boolean
): MetaDataT {
  const { metadata } = getAnswersFromAnswerMap({ productId, answers, keys: [metadataId] });
  return parseMetaData<MetaDataT>(metadata, validate);
}

export interface HpSetMetaDataInAnswersProps<MetaDataT extends HpMetaData> {
  answers: HpAnswerMap;
  deleteKeyIfNullOrUndefined?: boolean;
  metadata: Partial<MetaDataT>;
  productId: string;
  /**
   * Validate existing metadata when retrieving from answers?
   */
  validate?: boolean;
}

export function setMetaDataInAnswers<MetaDataT extends HpMetaData>({
  answers,
  deleteKeyIfNullOrUndefined = false,
  metadata,
  productId,
  validate = true
}: HpSetMetaDataInAnswersProps<MetaDataT>): void {
  const { metadata: dataAnswer } = getAnswersFromAnswerMap({ productId, answers, keys: [metadataId] });
  const data: MetaDataT = dataAnswer ? parseMetaData(dataAnswer, validate) : ({} as MetaDataT);
  const keysToAdd = lodash.keys(metadata);
  if (!keysToAdd.find((k) => k === 'versionHistory')) {
    keysToAdd.push('versionHistory');
  }

  keysToAdd.forEach((key) => {
    const answer = metadata[key];

    if (key === 'versionHistory') {
      // eslint-disable-next-line prefer-const
      let { versionHistory = {}, version, begin } = data;
      let created = false;
      if (!versionHistory[version]) {
        versionHistory[version] = begin || new Date().toISOString();
        created = true;
      }

      if (created || answer) {
        const versionsToAdd = answer as HpVersionHistoryMetaData;
        lodash.keys(versionsToAdd).forEach((version) => {
          if (!hasOwnProperty(versionHistory, version)) {
            versionHistory[version] = versionsToAdd[version] || new Date().toISOString();
          }
        });
        data.versionHistory = versionHistory;
      }
    } else if (isDefined(answer)) {
      data[key] = answer;
    } else if (deleteKeyIfNullOrUndefined) {
      delete data[key];
    }
  });
  const newData = createMetaData(data);
  const metadataAnswerId = getQuestionIdFor(productId, metadataId);
  delete answers[metadataAnswerId]; // prevent state mutation.
  setCustomerAnswer(answers, metadataAnswerId, newData);
}

function createMetaData<MetaDataT extends HpMetaData>(props: MetaDataT): string {
  // TODO Remove this when we're confident that it's not needed.
  ensureDefined(props.productId, 'props.productId');
  ensureDefined(props.questionSetId, 'props.questionSetId');
  props.begin = props.begin || new Date().toISOString();

  const keys = lodash.keys(props).sort();
  const data: Record<string, any> = {};
  keys.forEach((key) => {
    data[key] = props[key];
  });

  return JSON.stringify(data);
}

export function parseMetaData<MetaDataT extends HpMetaData>(
  metadata: string | undefined,
  validate?: boolean
): MetaDataT {
  const data = JSON.parse(ensureDefined(metadata, 'metadata')) as MetaDataT;

  if (validate !== false) {
    // TODO Remove this when we're confident that it's not needed.
    ensureDefined(data.productId, 'metadata.productId');
    ensureDefined(data.questionSetId, 'metadata.questionSetId');
  }

  return data;
}

/**
 * Used to convert a strongly typed object into a JSON-like string for output.
 * The advantage of doing things like this is that if a variable name changes, it will be changed in the output too.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function objectToErrorOutput<DataT extends object>(data: DataT): string {
  return lodash
    .trim(JSON.stringify(data, null, 1), '"')
    .replace(/\n/g, '')
    .replace(/{ /g, '(')
    .replace(/}/g, ')')
    .replace(/"/g, "'");
}

/**
 * This aids debugging but shouldn't be used in production envioronments
 * @returns a new map containing the answers provided with keys sorted alphabetically.
 */
export function sortAnswerMap(answers: HpAnswerMap): HpAnswerMap {
  return sortKeysInMap(answers, false);
}

/**
 * This only sorts keys at the top-most level, unless 'isRecursive' is true.
 * @returns a new map containing the data provided with keys sorted alphabetically.
 */
export function sortKeysInMap<ValueT>(data: SMap<ValueT>, isRecursive?: boolean): SMap<ValueT> {
  if (!data || typeof data !== 'object' || data.constructor !== Object) {
    return data;
  }

  const keys = lodash.keys(data).sort();
  const res: SMap<ValueT> = {};
  if (!isRecursive) {
    keys.forEach((key) => {
      res[key] = data[key];
    });
  } else {
    keys.forEach((key) => {
      let item: any = data[key];
      if (lodash.isArray(item)) {
        item = item.map((x) => sortKeysInMap(x, isRecursive));
      } else {
        item = sortKeysInMap(item, isRecursive);
      }

      res[key] = item;
    });
  }

  return res;
}

export { getQuestionIdFor };
